import { useReducer, useEffect } from 'react';
import { produce } from 'immer';
import { v4 as uuid } from 'uuid';

const DEFAULT_ERROR = 'There was an error!';

export interface State<E> {
  data: E;
  isLoading: boolean;
  error: any;
  nonce?: string;
}

export type RequestRunner = (
  req: () => Promise<any>,
  transformFn?: any
) => void;

export interface RunnerSegment {
  run: () => void;
}

export interface IsLoadingSegment {
  isLoading: boolean;
}

export type DataSetter<E> = (E) => E;

export interface RequestResult<E> {
  data: E;
  isLoading: boolean;
  error: any;
  run: RequestRunner;
  resetRequest?: () => void;
  dispatch: React.Dispatch<any>;
  setData: (data: E | DataSetter<E>) => void;
}

export enum RequestAction {
  Init = 'SET_INIT',
  Reset = 'SET_RESET',
  Success = 'SET_SUCCESS',
  Error = 'SET_ERROR',
  Update = 'SET_DATA',
  UpdateViaFn = 'SET_DATA_VIA_FN',
  Nonce = 'SET_NONCE'
}

const reducer = produce((draft, action) => {
  switch (action.type) {
    case RequestAction.Nonce:
      draft.nonce = action.nonce;
      return;
    case RequestAction.Init:
      draft.isLoading = true;
      draft.error = null;
      return;
    case RequestAction.Success:
      if (draft.nonce == null || action.nonce === draft.nonce) {
        draft.isLoading = false;
        draft.data = action.data;
        draft.error = null;
      }
      return;
    case RequestAction.Error:
      if (draft.nonce == null || action.nonce === draft.nonce) {
        draft.isLoading = false;
        draft.error = action.message || DEFAULT_ERROR;
      }
      return;
    case RequestAction.UpdateViaFn:
      draft.data = produce(draft.data, action.updateFn);
      return;
    case RequestAction.Update:
      draft.data = action.data;
      return;
  }
});

async function resolve<R, E>(
  dispatch: React.Dispatch<any>,
  req: () => Promise<any>,
  transformFn?: (response: R) => E | Promise<E>,
  isTransformAsync = false,
  isUnmounted?: boolean
): Promise<void> {
  const nonce: string = uuid();
  try {
    dispatch({
      type: RequestAction.Nonce,
      nonce
    });
    let response: any = await req();
    if (!isUnmounted) {
      if (response.status === 'OK') {
        if (transformFn) {
          if (isTransformAsync) {
            response = await transformFn(response);
          } else {
            response = transformFn(response);
          }
        }
        dispatch({
          type: RequestAction.Success,
          data: response,
          nonce
        });
      } else {
        dispatch({
          type: RequestAction.Error,
          message: response.message,
          nonce
        });
      }
    }
  } catch (err) {
    console.error(err);
    if (!isUnmounted) {
      dispatch({
        type: RequestAction.Error,
        message: err.message,
        nonce
      });
    }
  }
}

interface UseRequestParams<R, E> {
  request: () => Promise<any>;
  transformFn?: (response: R) => E | Promise<E>;
  isTransformAsync?: boolean;
  enabled?: boolean;
}

/**
 * Used to hook an AJAX request into a component's render lifecycle
 *
 * run: allows for running the same or a different query based on an action, like an onClick
 * setData: allows for updating state.data
 *
 * @param request a function that returns a PostJSON / GetJSON promise call, usually wrapped in React.useCallback
 * @param transformFn a function that transforms the response from the PostJSON / GetJSON call
 * @param enabled use this boolean to enable / disable the useEffect hook from running
 */
function useRequestJSON<R, E = R>({
  request,
  transformFn,
  isTransformAsync = false,
  enabled = true
}: UseRequestParams<R, E>): RequestResult<E> {
  const initialState: State<E> = {
    data: null,
    isLoading: true,
    error: null,
    nonce: null
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  let isUnmounted = false;

  const initRequest = () => {
    dispatch({ type: RequestAction.Init });
  };

  useEffect(() => {
    if (enabled && !isUnmounted) {
      initRequest();
      resolve<R, E>(
        dispatch,
        request,
        transformFn,
        isTransformAsync,
        isUnmounted
      );
    }
    return () => {
      isUnmounted = true;
    };
  }, [request, enabled]);

  return {
    ...state,
    run(req: () => Promise<any>, transformFn: any) {
      if (!isUnmounted) {
        initRequest();
        resolve<R, E>(
          dispatch,
          req,
          transformFn,
          isTransformAsync,
          isUnmounted
        );
      }
    },
    dispatch,
    setData(data: E) {
      if (!isUnmounted) {
        dispatch({
          type: RequestAction.Update,
          data
        });
      }
    }
  };
}

const onEventReducer = produce((draft, action) => {
  switch (action.type) {
    case RequestAction.Init:
      draft.isLoading = true;
      draft.error = null;
      return;
    case RequestAction.Reset:
      draft.isLoading = false;
      draft.data = null;
      draft.error = null;
      return;
    case RequestAction.Success:
      draft.isLoading = false;
      draft.data = action.data;
      draft.error = null;
      return;
    case RequestAction.Error:
      draft.isLoading = false;
      draft.error = action.message || DEFAULT_ERROR;
      return;
    case RequestAction.Update:
      draft.data = action.data;
      return;
  }
});

/**
 * Used for AJAX requests that occur exclusively via an event,
 * such as a click handler.
 *
 * The difference from the above hook is that there is no useEffect,
 * so the AJAX request won't fire until run() is called explicitly.
 */
export function useRequestOnEvent<R, E>(): RequestResult<E> {
  const initialState: State<E> = {
    data: null,
    isLoading: false,
    error: null
  };
  const [state, dispatch] = useReducer(onEventReducer, initialState);

  const initRequest = () => {
    dispatch({ type: RequestAction.Init });
  };

  const resetRequest = () => {
    dispatch({ type: RequestAction.Reset });
  };

  return {
    ...state,
    resetRequest,
    run(req: () => Promise<any>, transformFn?: any) {
      initRequest();
      resolve<R, E>(dispatch, req, transformFn);
    }
  };
}

export default useRequestJSON;
